import {
  concat,
  difference,
  every,
  filter,
  isEmpty,
  join,
  map,
  mapValues,
  startsWith,
  uniqBy,
} from 'es-toolkit/compat';
import {
  AsyncBuffer,
  FileMetaData,
  parquetMetadataAsync,
  parquetRead,
  parquetReadObjects,
  parquetSchema,
} from 'hyparquet';
import { compressors } from 'hyparquet-compressors';

import { markerPositivityColumnPrefix } from './constants';
import { ParquetRequestError } from './requestError';
import { getParquetResourceId, measureParquetStage, measureParquetStageAsync } from './instrumentation';
import {
  asyncBufferFromUrlWithGetFallback,
  cachedAsyncBufferWithFixedWindows,
  defaultParquetRangeWindowSize,
  fetchParquetFileTail,
} from './rangeBuffer';
import {
  ColumnarParquetData,
  ParquetColumn,
  ParquetFile,
  ParquetMetadataReadRequest,
  ParquetRow,
  ParquetRowsReadRequest,
} from './types';

const getParquetMetadata = async (
  { parquetFileUrl, byteLength, headUrl, minSize = defaultParquetRangeWindowSize }: ParquetMetadataReadRequest,
  thread: 'main' | 'worker' = 'main'
): Promise<{
  file: Awaited<ReturnType<typeof asyncBufferFromUrlWithGetFallback>>;
  metadata: FileMetaData;
}> => {
  const resourceId = getParquetResourceId(parquetFileUrl);
  const tail = byteLength ? undefined : await fetchParquetFileTail(parquetFileUrl, minSize);
  const uncachedFile = await asyncBufferFromUrlWithGetFallback(parquetFileUrl, byteLength ?? tail?.byteLength, headUrl);
  const file = cachedAsyncBufferWithFixedWindows(uncachedFile, { minSize, resourceId, thread, seed: tail });
  let metadata: FileMetaData;
  try {
    metadata = await measureParquetStageAsync({ resourceId, stage: 'metadata-decode', thread }, () =>
      parquetMetadataAsync(file)
    );
  } catch (error) {
    if (error instanceof ParquetRequestError) throw error;
    throw new Error(`Failed to read parquet metadata for ${resourceId}`);
  }
  if (!metadata?.schema) throw new Error(`Parquet metadata has no schema for ${resourceId}`);
  return { file, metadata };
};

export const readParquetMetadataFromUrl = async (
  request: ParquetMetadataReadRequest,
  thread: 'main' | 'worker' = 'main'
): Promise<Pick<FileMetaData, 'schema' | 'num_rows'>> => {
  const { metadata } = await getParquetMetadata(request, thread);
  return { schema: metadata.schema, num_rows: metadata.num_rows };
};

const isCloneSafeValue = (value: unknown, seen: Set<object>): boolean => {
  if (value === null || ['string', 'number', 'boolean', 'bigint', 'undefined'].includes(typeof value)) return true;
  if (typeof value !== 'object' || seen.has(value)) return false;

  seen.add(value);
  const isArray = Array.isArray(value);
  const isPlainObject = Object.getPrototypeOf(value) === Object.prototype;
  if (!isArray && !isPlainObject) return false;
  return Object.values(value).every((nestedValue) => isCloneSafeValue(nestedValue, seen));
};

export const assertCloneSafeRowContext = (rowContext: Record<string, any>) => {
  if (!isCloneSafeValue(rowContext, new Set())) throw new Error('Parquet row context is not clone-safe');
};

const assertColumnsExist = (metadata: FileMetaData, columns: string[] | undefined, resourceId: string) => {
  if (!columns || isEmpty(columns)) return;
  const missingColumns = difference(columns, map(metadata.schema, 'name'));
  if (!isEmpty(missingColumns)) {
    throw new Error(
      `Columns ${join(missingColumns, ', ')} not found in parquet resource ${resourceId}. Available columns: ${join(
        map(metadata.schema, 'name'),
        ', '
      )}`
    );
  }
};

const decodeParquetRows = async ({
  file,
  metadata,
  columns,
  resourceId,
  thread,
}: {
  file: AsyncBuffer;
  metadata: FileMetaData;
  columns?: string[];
  resourceId: string;
  thread: 'main' | 'worker';
}): Promise<Record<string, unknown>[]> => {
  try {
    return await measureParquetStageAsync(
      {
        resourceId,
        stage: 'row-decode',
        thread,
        rowCount: Number(metadata.num_rows),
        columnCount: columns?.length ?? metadata.schema.length - 1,
      },
      () => parquetReadObjects({ file, metadata, columns, compressors })
    );
  } catch (error) {
    if (error instanceof ParquetRequestError) throw error;
    throw new Error(`Failed to decode parquet rows for ${resourceId}`);
  }
};

export const readParquetFromUrl = async (
  {
    parquetFileUrl,
    columnsForQuery: columns,
    additionalRowContext = {},
    outputFormat,
    ...metadataRequest
  }: ParquetRowsReadRequest,
  thread: 'main' | 'worker' = 'main'
): Promise<ParquetFile> => {
  assertCloneSafeRowContext(additionalRowContext);
  const resourceId = getParquetResourceId(parquetFileUrl);
  const { file, metadata } = await getParquetMetadata({ parquetFileUrl, ...metadataRequest }, thread);
  assertColumnsExist(metadata, columns, resourceId);
  const decodedRows = await decodeParquetRows({ file, metadata, columns, resourceId, thread });
  return {
    rows: (outputFormat === 'columnar'
      ? decodedRows
      : map(decodedRows, (row) => ({ ...row, ...additionalRowContext }))) as ParquetRow[],
    metadata,
    columnsQueried: columns ?? map(metadata.schema.slice(1), 'name'),
  };
};

export const normalizeParquetRows = ({
  rows,
  resourceId,
  columnCount,
  thread = 'main',
}: {
  rows?: ParquetRow[];
  resourceId: string;
  columnCount: number;
  thread?: 'main' | 'worker';
}): ParquetRow[] =>
  measureParquetStage(
    { resourceId, stage: 'row-normalization', thread, rowCount: rows?.length ?? 0, columnCount },
    () =>
      map(rows, (row) =>
        mapValues(row, (value, columnName) => {
          if (typeof value === 'bigint') return Number(value);
          if (startsWith(columnName, markerPositivityColumnPrefix)) return Boolean(value);
          return value;
        })
      ) as ParquetRow[]
  );

const columnValidity = (values: unknown[]): Uint8Array | undefined => {
  const validity = Uint8Array.from(values, (value) => Number(value !== null && value !== undefined));
  return validity.includes(0) ? validity : undefined;
};

const normalizeColumnValue = (value: unknown, columnName: string): unknown => {
  if (typeof value === 'bigint') return Number(value);
  if (startsWith(columnName, markerPositivityColumnPrefix)) return Boolean(value);
  return value;
};

/**
 * Converts hyparquet's temporary row objects into the compact representation
 * retained by columnar consumers. The conversion runs in the worker; its typed
 * buffers are then transferred rather than cloned to the main thread.
 *
 * Keep row-oriented output for consumers that need to iterate complete records.
 * This representation is valuable when large datasets are consumed column by
 * column or passed to GPU attributes, and rows are reconstructed only rarely.
 */
export const parquetRowsToColumnarData = ({
  rows,
  columns,
  rowContext = {},
}: {
  rows: Record<string, unknown>[];
  columns: string[];
  rowContext?: Record<string, any>;
}): ColumnarParquetData => {
  // Float32 keeps slide-pixel precision and lets deck.gl skip its 64-bit high/low split.
  const positions = new Float32Array(rows.length * 2);
  for (let index = 0; index < rows.length; index += 1) {
    positions[index * 2] = Number(rows[index]?.X ?? 0);
    positions[index * 2 + 1] = Number(rows[index]?.Y ?? 0);
  }

  const encodedColumns = Object.fromEntries(
    map(
      columns.filter((columnName) => columnName !== 'X' && columnName !== 'Y'),
      (columnName): [string, ParquetColumn] => {
        const values = map(rows, (row) => normalizeColumnValue(row?.[columnName], columnName));
        const validity = columnValidity(values);
        const populatedValues = values.filter((value) => value !== null && value !== undefined);

        if (populatedValues.every((value) => typeof value === 'number')) {
          return [
            columnName,
            {
              kind: 'number',
              values: Float64Array.from(values, (value) => (typeof value === 'number' ? value : 0)),
              validity,
            },
          ];
        }
        if (populatedValues.every((value) => typeof value === 'boolean')) {
          return [
            columnName,
            {
              kind: 'boolean',
              values: Uint8Array.from(values, (value) => Number(Boolean(value))),
              validity,
            },
          ];
        }
        if (populatedValues.every((value) => typeof value === 'string')) {
          const dictionary = Array.from(new Set(populatedValues as string[]));
          const dictionaryIndexes = new Map(map(dictionary, (value, index) => [value, index]));
          const valueCounts = new Uint32Array(dictionary.length);
          return [
            columnName,
            {
              kind: 'dictionary',
              values: Uint32Array.from(values, (value) => {
                if (typeof value !== 'string') return 0;
                const dictionaryIndex = dictionaryIndexes.get(value) ?? 0;
                valueCounts[dictionaryIndex] += 1;
                return dictionaryIndex;
              }),
              dictionary,
              validity,
              valueCounts,
            },
          ];
        }
        return [columnName, { kind: 'values', values }];
      }
    )
  );

  return { length: rows.length, positions, columns: encodedColumns, rowContext };
};

const maxRowGroupsInFlight = 3;

export type ParquetLoadProgressCallback = (loadedRows: number, totalRows: number) => void;

interface DecodedColumnChunk {
  columnName: string;
  columnData: ArrayLike<unknown>;
  rowStart: number;
}

/** Same encoding as `parquetRowsToColumnarData`; `write` returns false when the column needs the `values` fallback. */
const createStreamedColumn = (columnName: string, rowCount: number) => {
  const validity = new Uint8Array(rowCount);
  const dictionary: string[] = [];
  const dictionaryIndexes = new Map<string, number>();
  const dictionaryCounts: number[] = [];
  let kind: 'number' | 'boolean' | 'dictionary' | undefined;
  let values: Float64Array | Uint8Array | Uint32Array | undefined;
  let hasMissingValue = false;
  let writtenRows = 0;

  const write = (rowIndex: number, rawValue: unknown): boolean => {
    writtenRows += 1;
    const value = normalizeColumnValue(rawValue, columnName);
    if (value === null || value === undefined) {
      hasMissingValue = true;
      return true;
    }
    const valueKind =
      typeof value === 'number'
        ? 'number'
        : typeof value === 'boolean'
        ? 'boolean'
        : typeof value === 'string'
        ? 'dictionary'
        : undefined;
    if (!valueKind || (kind && kind !== valueKind)) return false;
    if (!kind) {
      kind = valueKind;
      values =
        kind === 'number'
          ? new Float64Array(rowCount)
          : kind === 'boolean'
          ? new Uint8Array(rowCount)
          : new Uint32Array(rowCount);
    }
    validity[rowIndex] = 1;
    if (kind === 'dictionary') {
      let dictionaryIndex = dictionaryIndexes.get(value as string);
      if (dictionaryIndex === undefined) {
        dictionaryIndex = dictionary.length;
        dictionary.push(value as string);
        dictionaryIndexes.set(value as string, dictionaryIndex);
        dictionaryCounts.push(0);
      }
      dictionaryCounts[dictionaryIndex] += 1;
      values![rowIndex] = dictionaryIndex;
    } else {
      values![rowIndex] = Number(value);
    }
    return true;
  };

  const build = (): ParquetColumn => {
    const missingValues = hasMissingValue ? validity : undefined;
    if (kind === 'boolean') return { kind, values: values as Uint8Array, validity: missingValues };
    if (kind === 'dictionary') {
      return {
        kind,
        values: values as Uint32Array,
        dictionary,
        validity: missingValues,
        valueCounts: Uint32Array.from(dictionaryCounts),
      };
    }
    return { kind: 'number', values: (values as Float64Array) ?? new Float64Array(rowCount), validity: missingValues };
  };

  return { write, build, isComplete: () => writtenRows === rowCount };
};

const canStreamColumns = (metadata: FileMetaData, columns: string[] | undefined): columns is string[] => {
  if (!columns || isEmpty(columns)) return false;
  const topLevelColumns = new Map(map(parquetSchema(metadata).children, (child) => [child.element.name, child]));
  return every(columns, (columnName) => {
    const column = topLevelColumns.get(columnName);
    return Boolean(column) && isEmpty(column!.children) && column!.element.repetition_type !== 'REPEATED';
  });
};

/** Decodes one row group at a time into typed arrays, without per-row objects; undefined means use the row path. */
const streamParquetColumns = async ({
  file,
  metadata,
  columns,
  rowContext,
  onProgress,
}: {
  file: AsyncBuffer;
  metadata: FileMetaData;
  columns: string[];
  rowContext: Record<string, any>;
  onProgress?: ParquetLoadProgressCallback;
}): Promise<ColumnarParquetData | undefined> => {
  const rowCount = Number(metadata.num_rows);
  const positions = new Float32Array(rowCount * 2);
  const encodedColumnNames = filter(columns, (columnName) => columnName !== 'X' && columnName !== 'Y');
  const streamedColumns = new Map(
    map(encodedColumnNames, (columnName) => [columnName, createStreamedColumn(columnName, rowCount)])
  );
  const coordinateRowsWritten = { X: 0, Y: 0 };
  const rowGroups = metadata.row_groups.reduce<{ rowStart: number; rowEnd: number }[]>((groups, group) => {
    const rowStart = groups.length ? groups[groups.length - 1].rowEnd : 0;
    return [...groups, { rowStart, rowEnd: rowStart + Number(group.num_rows) }];
  }, []);

  const readRowGroup = ({ rowStart, rowEnd }: { rowStart: number; rowEnd: number }) => {
    const chunks: DecodedColumnChunk[] = [];
    return parquetRead({
      file,
      metadata,
      columns,
      rowStart,
      rowEnd,
      compressors,
      onChunk: (chunk) => chunks.push(chunk),
    }).then(() => chunks);
  };

  const writeChunk = (
    { columnName, columnData, rowStart }: DecodedColumnChunk,
    groupStart: number,
    groupEnd: number
  ): boolean => {
    const coordinateOffset = columnName === 'X' ? 0 : columnName === 'Y' ? 1 : -1;
    const streamedColumn = streamedColumns.get(columnName);
    for (let index = 0; index < columnData.length; index += 1) {
      const rowIndex = rowStart + index;
      if (rowIndex < groupStart || rowIndex >= groupEnd) continue;
      const value = columnData[index];
      if (coordinateOffset >= 0) {
        positions[rowIndex * 2 + coordinateOffset] = Number(value ?? 0);
        coordinateRowsWritten[columnName as 'X' | 'Y'] += 1;
      }
      if (streamedColumn && !streamedColumn.write(rowIndex, value)) return false;
    }
    return true;
  };

  const groupsInFlight: Promise<DecodedColumnChunk[]>[] = [];
  let nextGroupToRead = 0;
  for (const { rowStart, rowEnd } of rowGroups) {
    while (nextGroupToRead < rowGroups.length && groupsInFlight.length < maxRowGroupsInFlight) {
      const groupRead = readRowGroup(rowGroups[nextGroupToRead]);
      // Later groups may reject after an earlier failure has already ended this read.
      groupRead.catch(() => undefined);
      groupsInFlight.push(groupRead);
      nextGroupToRead += 1;
    }
    const chunks = await groupsInFlight.shift()!;
    // Writing in row order keeps dictionary order identical to the row-based conversion.
    chunks.sort((first, second) => first.rowStart - second.rowStart);
    for (const chunk of chunks) {
      if (!writeChunk(chunk, rowStart, rowEnd)) return undefined;
    }
    onProgress?.(rowEnd, rowCount);
  }

  const coordinatesComplete = every(
    filter(['X', 'Y'] as const, (columnName) => columns.includes(columnName)),
    (columnName) => coordinateRowsWritten[columnName] === rowCount
  );
  if (!coordinatesComplete || !every([...streamedColumns.values()], (column) => column.isComplete())) {
    return undefined;
  }
  return {
    length: rowCount,
    positions,
    columns: Object.fromEntries(
      map(encodedColumnNames, (columnName) => [columnName, streamedColumns.get(columnName)!.build()])
    ),
    rowContext,
  };
};

/** Columnar read; nested or mixed-type columns fall back to the row-based conversion with identical output. */
export const readParquetColumnarFromUrl = async (
  {
    parquetFileUrl,
    columnsForQuery: columns,
    additionalRowContext = {},
    outputFormat: _outputFormat,
    ...metadataRequest
  }: ParquetRowsReadRequest,
  thread: 'main' | 'worker' = 'main',
  onProgress?: ParquetLoadProgressCallback
): Promise<{ columnarData: ColumnarParquetData; metadata: FileMetaData; columnsQueried: string[] }> => {
  assertCloneSafeRowContext(additionalRowContext);
  const resourceId = getParquetResourceId(parquetFileUrl);
  const { file, metadata } = await getParquetMetadata({ parquetFileUrl, ...metadataRequest }, thread);
  assertColumnsExist(metadata, columns, resourceId);
  const columnsQueried = columns ?? map(metadata.schema.slice(1), 'name');

  if (canStreamColumns(metadata, columns)) {
    let streamedData: ColumnarParquetData | undefined;
    try {
      streamedData = await measureParquetStageAsync(
        {
          resourceId,
          stage: 'row-decode',
          thread,
          rowCount: Number(metadata.num_rows),
          columnCount: columns.length,
        },
        () => streamParquetColumns({ file, metadata, columns, rowContext: additionalRowContext, onProgress })
      );
    } catch (error) {
      if (error instanceof ParquetRequestError) throw error;
      throw new Error(`Failed to decode parquet rows for ${resourceId}`);
    }
    if (streamedData) return { columnarData: streamedData, metadata, columnsQueried };
  }

  const decodedRows = await decodeParquetRows({ file, metadata, columns, resourceId, thread });
  return {
    columnarData: parquetRowsToColumnarData({
      rows: decodedRows,
      columns: columnsQueried,
      rowContext: additionalRowContext,
    }),
    metadata,
    columnsQueried,
  };
};

export const getColumnarValue = (column: ParquetColumn | undefined, index: number): unknown => {
  if (!column || (column.kind !== 'values' && column.validity?.[index] === 0)) return undefined;
  if (column.kind === 'dictionary') return column.dictionary[column.values[index]];
  if (column.kind === 'boolean') return Boolean(column.values[index]);
  return column.values[index];
};

export const getColumnarParquetRow = (data: ColumnarParquetData, index: number): ParquetRow =>
  ({
    X: data.positions[index * 2],
    Y: data.positions[index * 2 + 1],
    ...mapValues(data.columns, (column) => getColumnarValue(column, index)),
    ...data.rowContext,
  } as ParquetRow);

export const mergeColumnarParquetData = (
  existingData: ColumnarParquetData,
  newData: ColumnarParquetData
): ColumnarParquetData => {
  if (existingData.length !== newData.length) throw new Error('Cannot merge parquet columns with different row counts');
  return {
    length: existingData.length,
    positions: existingData.positions.length ? existingData.positions : newData.positions,
    columns: { ...existingData.columns, ...newData.columns },
    rowContext: { ...existingData.rowContext, ...newData.rowContext },
  };
};

export const mergeParquetFiles = ({
  existingData,
  newData,
  resourceId,
  thread = 'main',
}: {
  existingData: ParquetFile;
  newData: ParquetFile;
  resourceId: string;
  thread?: 'main' | 'worker';
}): ParquetFile => ({
  rows:
    existingData.columnarData && newData.columnarData
      ? []
      : measureParquetStage(
          {
            resourceId,
            stage: 'incremental-merge',
            thread,
            rowCount: existingData.rows.length,
            columnCount: newData.columnsQueried.length,
          },
          () =>
            map(
              existingData.rows,
              (row, index): ParquetRow =>
                ({
                  ...(row || {}),
                  ...(newData.rows[index] || {}),
                } as ParquetRow)
            )
        ),
  columnarData:
    existingData.columnarData && newData.columnarData
      ? mergeColumnarParquetData(existingData.columnarData, newData.columnarData)
      : undefined,
  columnsQueried: concat(existingData.columnsQueried, newData.columnsQueried),
  metadata: {
    ...existingData.metadata,
    schema: uniqBy(concat(existingData.metadata.schema, newData.metadata.schema), 'name'),
  },
});
